import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import {
  Sparkles, Heart, Users, Calculator, Flame, Crown, ScrollText, Compass,
  CalendarCheck, Stars, Hash, MoonStar, Sun, Diamond, Activity, Briefcase,
  Coins, Search, ArrowRight, Lock, Gauge, Wand2, Telescope, Rocket,
  Gift, Layers, Music2, Zap, Trophy, Camera, Eye, Radar, Flower, Baby,
} from "lucide-react";

/**
 * liveSocialProof — takes a "12,600 reports generated" style string and
 * bumps the leading number by a small session-random amount so the count
 * feels alive on every page-load without being chaotic.
 *   base * (0.1%-0.5%) + small salt → drift of ~15-70 for a 12,600 base.
 */
function liveSocialProof(str) {
  if (!str) return str;
  const m = str.match(/^([\d,]+)(.*)/);
  if (!m) return str;
  const base = parseInt(m[1].replace(/,/g, ""), 10);
  if (!Number.isFinite(base) || base <= 0) return str;
  const bump = Math.floor(base * (0.001 + Math.random() * 0.004)) + Math.floor(Math.random() * 8);
  return (base + bump).toLocaleString("en-IN") + m[2];
}

/**
 * TOOL CATALOG
 * Each tool gets:
 *  - distinctive gradient (avoid the AI-slop purple-on-white default)
 *  - Lucide icon (no emojis)
 *  - one-liner pitch
 *  - price tag (free / ₹333 / ₹1700)
 *  - badge (NEW / FLAGSHIP / FREE / POPULAR)
 *  - category
 */
const TOOLS = [
  // ─────── FLAGSHIP PREMIUM — surfaced first by user request ───────
  {
    slug: "twenty_year", path: "/twenty-year-prediction", category: "power",
    title: "20-Year Forecast", icon: ScrollText,
    pitch: "55+ pages year-by-year life report — career, marriage, wealth, health, timing across two decades.",
    grad: "from-amber-500 via-orange-600 to-rose-600",
    badge: "😊 10 LAKH HAPPY CUSTOMERS", badgeColor: "bg-gradient-to-r from-yellow-300 to-amber-400 text-amber-950",
    iconBg: "bg-amber-500/25",
    price: "₹899", anchorPrice: "₹5500",
    ctaText: "See My 20-Year Story",
    socialProof: "12,600 reports generated",
    flagship: true, pinned: true,
  },

  // ─────── FOUNDATIONS ───────
  { slug: "kundali", path: "/kundali-calculator", category: "foundations",
    title: "Free Kundali", icon: Stars,
    pitch: "Lahiri sidereal birth chart, planets, lagna, panchang — fully free.",
    grad: "from-amber-400 via-orange-500 to-rose-500",
    badge: "FREE", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-amber-500/20",
    ctaText: "Get My Free Chart",
    socialProof: "48,200 charts generated" },
  { slug: "horoscope", path: "/horoscope", category: "foundations",
    title: "Today's Horoscope", icon: Sun,
    pitch: "Personalized daily brief with transits, choghadiya, do/don't windows.",
    grad: "from-yellow-400 via-amber-500 to-orange-600",
    badge: "FREE", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-yellow-500/25",
    ctaText: "Read Today's Brief",
    socialProof: "9,340 read today" },
  { slug: "navamsa", path: "/navamsa", category: "foundations",
    title: "D9 Navamsa", icon: Heart,
    pitch: "Marriage karma, spouse profile, post-30 manifestation chart.",
    grad: "from-rose-400 via-pink-500 to-fuchsia-500",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-rose-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Meet My Future Spouse",
    socialProof: "6,120 readings unlocked" },
  { slug: "divisional", path: "/divisional-charts", category: "foundations",
    title: "Divisional Charts (D1–D60)", icon: Layers,
    pitch: "Switch between 19 vargas to read every life-domain in depth.",
    grad: "from-indigo-500 via-violet-600 to-purple-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-indigo-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Unlock My 19 Charts",
    socialProof: "4,780 charts unlocked" },

  // ─────── DEEP READINGS ───────
  { slug: "nakshatra", path: "/nakshatra", category: "deep",
    title: "Nakshatra Deep Read", icon: MoonStar,
    pitch: "27-star deep analysis — your psyche, gana, yoni, naadi & deity.",
    grad: "from-cyan-400 via-sky-500 to-indigo-600",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-sky-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Decode My Birth Star",
    socialProof: "8,910 readings unlocked" },
  { slug: "bhrigu", path: "/bhrigu-bindu", category: "deep",
    title: "Bhrigu Bindu", icon: Telescope,
    pitch: "The exact karmic point where your dharma converges — Maharishi Bhrigu's method.",
    grad: "from-violet-500 via-purple-600 to-fuchsia-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-violet-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Find My Karmic Point",
    socialProof: "3,450 points revealed" },
  { slug: "rahu", path: "/rahu-placement", category: "deep",
    title: "Rahu Playbook", icon: Wand2,
    pitch: "Where Rahu wants you to break the family pattern — house/sign/star.",
    grad: "from-slate-600 via-zinc-700 to-stone-800",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-zinc-500/30",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Break My Family Pattern",
    socialProof: "2,860 playbooks unlocked" },
  { slug: "past_life", path: "/past-life", category: "deep",
    title: "Past Life Reading", icon: Gift,
    pitch: "D60 Shashtiamsa — what you brought into this life from the last.",
    grad: "from-emerald-500 via-teal-600 to-cyan-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-emerald-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "See My Past Life",
    socialProof: "5,240 lives read" },
  { slug: "saptavimshamsha", path: "/saptavimshamsha", category: "deep",
    title: "D27 Strength", icon: Activity,
    pitch: "Saptavimshamsha — chart that exposes your raw strength & stamina.",
    grad: "from-orange-500 via-red-600 to-rose-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-orange-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Test My Raw Strength",
    socialProof: "1,970 readings unlocked" },

  // ─────── POWER & WEALTH ───────
  { slug: "raja_yogas", path: "/raja-yogas", category: "power",
    title: "Raja Yogas", icon: Crown,
    pitch: "Royal yoga detector — power, authority & throne-style combinations in D1+D9.",
    grad: "from-amber-500 via-yellow-600 to-orange-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-amber-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Detect My Royal Yogas",
    socialProof: "3,680 yogas detected" },
  { slug: "ashtakavarga", path: "/ashtakavarga", category: "power",
    title: "Ashtakavarga", icon: Gauge,
    pitch: "Bindu-strength map — find your tailwind houses, avoid the headwind.",
    grad: "from-lime-500 via-green-600 to-emerald-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-lime-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Map My Power Houses",
    socialProof: "2,140 maps unlocked" },
  { slug: "varshaphal", path: "/varshaphal", category: "power",
    title: "Varshaphal", icon: CalendarCheck,
    pitch: "Annual solar-return reading — your year's muntha, dasha-bhukti & key combos.",
    grad: "from-fuchsia-500 via-pink-600 to-rose-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-fuchsia-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Preview My Year Ahead",
    socialProof: "4,510 years previewed" },
  { slug: "life_replay", path: "/life-replay", category: "power",
    title: "Life Replay · Karmic Timeline", icon: Music2,
    pitch: "Cinematic chapter-by-chapter timeline of your life — past chapters decoded, future eras previewed.",
    grad: "from-violet-500 via-fuchsia-600 to-pink-600",
    badge: "₹1100", badgeColor: "bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950",
    iconBg: "bg-violet-500/25",
    price: "₹1100", anchorPrice: "₹2999",
    ctaText: "Watch My Life Movie",
    socialProof: "1,830 replays unlocked",
    flagship: true },
  { slug: "astro_gps", path: "/astro-gps", category: "power",
    title: "Astro GPS · Best Cities", icon: Compass,
    pitch: "Astrocartography + Vedic Dig Bala — find your money / love / fame cities on a world map.",
    grad: "from-cyan-500 via-blue-600 to-indigo-700",
    badge: "₹1500", badgeColor: "bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950",
    iconBg: "bg-cyan-500/25",
    price: "₹1500", anchorPrice: "₹3999",
    ctaText: "Find My Money City",
    socialProof: "1,240 maps generated",
    flagship: true },
  { slug: "rudramsha", path: "/rudramsha", category: "power",
    title: "Rudramsha · D11 Wealth", icon: Coins,
    pitch: "D11 divisional chart of gains, networks & income streams — AI-personalised wealth blueprint.",
    grad: "from-amber-500 via-orange-600 to-rose-700",
    badge: "₹2500", badgeColor: "bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950",
    iconBg: "bg-amber-500/25",
    price: "₹2500", anchorPrice: "₹6999",
    ctaText: "Reveal My Wealth Blueprint",
    socialProof: "890 blueprints unlocked",
    flagship: true },
  { slug: "kalachakra_dasha", path: "/kalachakra-dasha", category: "power",
    title: "Kalachakra Dasha", icon: Rocket,
    pitch: "Wheel-of-time timeline — Jackpot & Fame windows + major life turnarounds.",
    grad: "from-indigo-600 via-blue-700 to-cyan-700",
    badge: "₹1700", badgeColor: "bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950",
    iconBg: "bg-indigo-500/30",
    price: "₹1700", anchorPrice: "₹4499",
    ctaText: "See My Turnaround Window",
    socialProof: "1,570 timelines unlocked",
    flagship: true },
  { slug: "dasha", path: "/dasha", category: "power",
    title: "Vimshottari Dasha", icon: Calculator,
    pitch: "Lifetime planetary periods (MD + AD) starting from your birth balance.",
    grad: "from-teal-500 via-cyan-600 to-blue-700",
    badge: "FREE", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-teal-500/25",
    ctaText: "See My Life Timeline",
    socialProof: "22,300 dashas computed" },

  // ─────── RELATIONSHIPS ───────
  { slug: "compatibility", path: "/compatibility", category: "relationships",
    title: "Compatibility · Guna Milan", icon: Users,
    pitch: "8-fold Ashtakoot match + Mangal-dosha check between two birth charts.",
    grad: "from-pink-500 via-rose-600 to-red-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-pink-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Match Our Charts",
    socialProof: "11,450 couples matched" },
  { slug: "ishta_devata", path: "/ishta-devata", category: "relationships",
    title: "Ishta Devata", icon: Diamond,
    pitch: "Your soul's chosen deity — 12th from Karakamsa + supporting Pala & Kula devatas.",
    grad: "from-purple-500 via-violet-600 to-indigo-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-purple-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Meet My Deity",
    socialProof: "3,970 deities revealed" },

  // ─────── KARMA & REMEDIES ───────
  { slug: "doshas", path: "/doshas", category: "karma",
    title: "Doshas & Remedies", icon: Flame,
    pitch: "11 classical dosha screens (Mangal, Kaal Sarp, Pitra…) + remedy playbook.",
    grad: "from-red-500 via-rose-600 to-pink-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-red-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Screen My Doshas",
    socialProof: "7,830 charts screened" },
  { slug: "chakras", path: "/chakras", category: "karma",
    title: "Chakra & Purushartha", icon: Zap,
    pitch: "Heat-map of your 7 chakras + Dharma/Artha/Kama/Moksha balance.",
    grad: "from-yellow-500 via-orange-600 to-red-600",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-orange-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Balance My Chakras",
    socialProof: "2,650 heat-maps unlocked" },
  { slug: "planet_effects", path: "/planet-effects", category: "karma",
    title: "9-Planet Effects & Remedies", icon: Sparkles,
    pitch: "Per-planet impact on your chart + mantra, gem & lifestyle remedies.",
    grad: "from-blue-500 via-indigo-600 to-violet-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-blue-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Get My Remedies",
    socialProof: "5,410 remedies unlocked" },
  { slug: "rashi_tulya_navamsa", path: "/rashi-tulya-navamsa", category: "karma",
    title: "Rashi Tulya Navamsa", icon: Compass,
    pitch: "D9 superimposed over D1 — see which dashas will fire which houses.",
    grad: "from-cyan-500 via-teal-600 to-emerald-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-cyan-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Overlay My D9",
    socialProof: "1,820 overlays unlocked" },

  // ─────── NUMEROLOGY ───────
  { slug: "name_numerology", path: "/name-numerology", category: "numerology",
    title: "Name Numerology", icon: Hash,
    pitch: "Chaldean name & destiny-number reading with vibration analysis.",
    grad: "from-emerald-500 via-green-600 to-lime-700",
    badge: "FREE", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-emerald-500/25",
    ctaText: "Read My Name",
    socialProof: "18,700 names decoded" },
  { slug: "numerology_deep", path: "/numerology-deep", category: "numerology",
    title: "Deep Numerology · Lo Shu Grid", icon: Calculator,
    pitch: "Full numerology + Lo Shu grid — strengths, missing numbers, repeats.",
    grad: "from-green-500 via-emerald-600 to-teal-700",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-green-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Grid My Numbers",
    socialProof: "6,340 grids unlocked" },

  // ─────── TIMING ───────
  { slug: "marriage_muhurat", path: "/marriage-muhurat", category: "timing",
    title: "Marriage Muhurat", icon: Heart,
    pitch: "Auspicious wedding dates for the couple — 12 classical Vedic filters + Abuja muhurats + Ask-any-date verdict.",
    grad: "from-rose-500 via-pink-600 to-fuchsia-600",
    badge: "FREE", badgeColor: "bg-emerald-300 text-emerald-950",
    iconBg: "bg-rose-500/25",
    ctaText: "Find Our Wedding Date",
    socialProof: "2,410 couples helped" },
  { slug: "baby_muhurat", path: "/tools/baby-muhurat", category: "timing",
    title: "Baby Delivery Muhurat", icon: Baby,
    pitch: "Planning a C-section? We check every 10-minute moment in your date range for the best birth time, using classical rules and lagna strength, and can also check the mother's Chandrashtama.",
    grad: "from-pink-500 via-rose-500 to-amber-500",
    badge: "₹5,000", badgeColor: "bg-pink-200 text-pink-950",
    iconBg: "bg-pink-500/25",
    ctaText: "Find The Birth Muhurat",
    socialProof: "Baby + mother + combined analysis" },
  { slug: "muhurat", path: "/muhurat", category: "timing",
    title: "Muhurat Finder", icon: CalendarCheck,
    pitch: "Best dates & time-windows for any activity — gold, home, job, marriage.",
    grad: "from-orange-500 via-amber-600 to-yellow-600",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-orange-500/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Find My Auspicious Time",
    socialProof: "3,120 muhurats found" },

  { slug: "pushkar", path: "/tools/pushkar", category: "deep",
    title: "Pushkar Navamsa & Bhaga", icon: Flower,
    pitch: "Which of your 9 planets sit in a Pushkar Navamsa arc or ±1° of the exact Pushkar Bhaga degree — the classical zones where planets deliver nourishment, protection and rare benefic power.",
    grad: "from-amber-500 via-yellow-600 to-orange-700",
    badge: "FREE", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-amber-500/25",
    ctaText: "Audit My Pushkar Zones",
    socialProof: "Classical Vedic system" },

  // ─────── CELEBRITY & FUN ───────
  { slug: "celebrities", path: "/celebrities", category: "celebrity",
    title: "Celebrity Chart Database", icon: Trophy,
    pitch: "1,500+ verified birth charts — Elon Musk, Steve Jobs, Ambani, Eminem, Osho, Queen Elizabeth II. See the exact Dasha that catapulted them.",
    grad: "from-yellow-500 via-amber-600 to-orange-700",
    badge: "FREE PREVIEW", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-yellow-500/25",
    ctaText: "Explore Famous Charts",
    socialProof: "34,800 charts opened" },
  { slug: "celebrity_pattern_recognizer", path: "/tools/celebrity-pattern-recognizer", category: "celebrity",
    title: "Celebrity Pattern Recognizer", icon: Radar,
    pitch: "Ask a question like “Where was Rahu in the Varshaphal when they won the lottery?” and see the pattern across every chart in a category, with colourful statistical charts.",
    grad: "from-emerald-500 via-teal-600 to-cyan-700",
    badge: "₹3,000 · 30 DAYS", badgeColor: "bg-gradient-to-r from-emerald-300 to-cyan-300 text-emerald-950",
    iconBg: "bg-emerald-500/25",
    ctaText: "Recognise Patterns",
    socialProof: "Zero-AI · pure statistics" },
  { slug: "celebrity_lookalike", path: "/tools/celebrity-lookalike", category: "celebrity",
    title: "Celebrity Lookalike", icon: Camera,
    pitch: "Match your chart's ascendant + planetary placements against 1,500+ verified celebrities and see who shares your cosmic signature.",
    grad: "from-fuchsia-500 via-pink-600 to-rose-700",
    badge: "NEW", badgeColor: "bg-gradient-to-r from-fuchsia-400 to-pink-500 text-white",
    iconBg: "bg-fuchsia-500/25",
    ctaText: "Find My Cosmic Twin",
    socialProof: "2,180 twins found" },

  // ─────── WEALTH DEEP-DIVES ───────
  { slug: "career_remedy", path: "/career-remedy", category: "power",
    title: "Career Remedy Nakshatra Secret", icon: Briefcase,
    pitch: "Find your 10th lord and its nakshatra, then unlock a secret nakshatra remedy: one of the most powerful ways to activate your career.",
    grad: "from-emerald-600 via-teal-700 to-cyan-800",
    badge: "₹1,500", badgeColor: "bg-emerald-200 text-emerald-950",
    iconBg: "bg-emerald-600/25",
    ctaText: "Find My Career Remedy",
    socialProof: "10th lord · nakshatra · secret remedy" },
  { slug: "bhagya_remedy", path: "/bhagya-remedy", category: "power",
    title: "Bhagya Unlock Remedy", icon: Sparkles,
    pitch: "Career or fortune stuck? Find the Pratyadhi Devatā of the 9th lord from your Moon, the deity whose mantra removes that block, and how to worship it.",
    grad: "from-amber-600 via-orange-700 to-rose-800",
    badge: "₹500", badgeColor: "bg-amber-200 text-amber-950",
    iconBg: "bg-amber-600/25",
    ctaText: "Unlock My Bhagya",
    socialProof: "9th lord from Moon · deity · mantra" },
  { slug: "d10_remedy", path: "/d10-remedy", category: "power",
    title: "D10 Career Activation Remedy", icon: Briefcase,
    pitch: "Sudden opposition at work despite a steady job? Find the D10 deity (Indra, Agni, Varuna, Kubera…) of your Mahadasha and Antardasha lords and the mantra to clear the obstacles.",
    grad: "from-sky-600 via-indigo-700 to-violet-800",
    badge: "₹500", badgeColor: "bg-sky-200 text-sky-950",
    iconBg: "bg-sky-600/25",
    ctaText: "Activate My Career",
    socialProof: "D10 · Mahadasha · Antardasha deities" },
  { slug: "wealth_hora", path: "/wealth-hora", category: "power",
    title: "Wealth Hora · D2", icon: Coins,
    pitch: "D2 Hora — the classical Vedic chart that maps your income streams, savings capacity and money-magnet houses.",
    grad: "from-yellow-600 via-amber-700 to-orange-800",
    badge: "₹333", badgeColor: "bg-amber-300 text-amber-950",
    iconBg: "bg-yellow-600/25",
    price: "₹333", anchorPrice: "₹999",
    ctaText: "Map My Money Chart",
    socialProof: "2,940 hora unlocked" },
  { slug: "wealth_programming", path: "/wealth-programming", category: "power",
    title: "Wealth Programming", icon: Zap,
    pitch: "AI-guided daily practice — reprogram your wealth-consciousness using your chart's exact karaka + dasha windows.",
    grad: "from-emerald-500 via-teal-600 to-cyan-700",
    badge: "NEW", badgeColor: "bg-gradient-to-r from-emerald-400 to-teal-500 text-white",
    iconBg: "bg-emerald-500/25",
    ctaText: "Rewire My Wealth Mind",
    socialProof: "1,420 practices started" },

  // ─────── VISUALS & LEARNING ───────
  { slug: "astro_visualise", path: "/visualise", category: "learning",
    title: "Astro Visualise", icon: Eye,
    pitch: "Watch your kundali animate through every Dasha — see planets ignite houses year-by-year in a cinematic timeline.",
    grad: "from-indigo-500 via-violet-600 to-fuchsia-700",
    badge: "PREVIEW", badgeColor: "bg-gradient-to-r from-indigo-400 to-violet-500 text-white",
    iconBg: "bg-indigo-500/25",
    ctaText: "Play My Life Movie",
    socialProof: "3,410 timelines watched" },
  { slug: "kp_astrology", path: "/kp-astrology", category: "learning",
    title: "KP Astrology · Sub-Lords", icon: Radar,
    pitch: "Krishnamurti Paddhati — precise 249-sub-lord chart for pinpointing event timing.",
    grad: "from-sky-500 via-blue-600 to-indigo-700",
    badge: "FREE", badgeColor: "bg-emerald-400 text-emerald-950",
    iconBg: "bg-sky-500/25",
    ctaText: "Read My Sub-Lords",
    socialProof: "5,680 charts computed" },
];

const CATEGORIES = [
  { key: "foundations",   label: "Foundations",          shortLabel: "Basics",         tag: "Start here", color: "from-amber-400 to-orange-500",   icon: Stars },
  { key: "deep",          label: "Deep Readings",        shortLabel: "Deep",           tag: "Soul & purpose", color: "from-violet-500 to-purple-700", icon: Telescope },
  { key: "power",         label: "Power & Wealth",       shortLabel: "Wealth",         tag: "Money, fame, timing", color: "from-yellow-500 to-amber-700", icon: Crown },
  { key: "relationships", label: "Relationships & Devotion", shortLabel: "Love",       tag: "Love & sadhana", color: "from-pink-500 to-rose-700", icon: Heart },
  { key: "karma",         label: "Karma & Remedies",     shortLabel: "Karma",          tag: "Heal & rebalance", color: "from-red-500 to-rose-700", icon: Flame },
  { key: "numerology",    label: "Numerology",           shortLabel: "Numbers",        tag: "Numbers & names", color: "from-emerald-500 to-green-700", icon: Hash },
  { key: "timing",        label: "Timing & Muhurat",     shortLabel: "Timing",         tag: "When to act", color: "from-orange-500 to-amber-600", icon: CalendarCheck },
  { key: "celebrity",     label: "Celebrity & Fun",      shortLabel: "Stars",          tag: "Verified famous charts", color: "from-fuchsia-500 to-pink-600", icon: Trophy },
  { key: "learning",      label: "Advanced & Visual",    shortLabel: "Visual",         tag: "See your chart in motion", color: "from-indigo-500 to-fuchsia-600", icon: Eye },
];

function ToolCard({ tool, index }) {
  const Icon = tool.icon;
  const featured = tool.pinned;
  const isPaid = !!tool.price;
  const cta = tool.ctaText || "Open";
  // Bump the social-proof count on every page-load so it feels alive
  const socialProofLive = React.useMemo(
    () => liveSocialProof(tool.socialProof),
    [tool.slug],
  );
  return (
    <Link
      to={tool.path}
      data-testid={`tool-card-${tool.slug}`}
      className={`group relative rounded-2xl overflow-hidden block transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-amber-400 ${
        featured
          ? "col-span-2 lg:col-span-3 xl:col-span-4 rounded-3xl ring-2 ring-amber-300/70 shadow-[0_10px_40px_-10px_rgba(255,140,60,0.7)]"
          : ""
      }`}
      style={{ animation: `tool-fade-in 0.45s ease-out both`, animationDelay: `${(index % 8) * 40}ms` }}
    >
      {/* solid gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${tool.grad}`} />

      {/* Featured: animated shimmer gradient overlay for extra vibrancy */}
      {featured && (
        <div
          className="absolute inset-0 opacity-70 mix-blend-overlay"
          style={{
            background: "linear-gradient(115deg, #fbbf24 0%, #f97316 20%, #ec4899 45%, #f97316 70%, #fbbf24 100%)",
            backgroundSize: "300% 100%",
            animation: "shimmer-band 7s linear infinite",
          }}
        />
      )}

      <div
        className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "3px 3px" }}
      />
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-white/0 via-white/0 to-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className={`relative flex flex-col text-white ${
        featured ? "p-5 sm:p-8 min-h-[240px] sm:min-h-[240px]" : "p-3 sm:p-6 min-h-[170px] sm:min-h-[220px]"
      }`}>
        {featured ? (
          <>
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
              <span className="text-[10px] sm:text-xs font-black tracking-widest px-2.5 sm:px-3 py-1 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg ring-1 ring-rose-200/70">❤️ MOST LIKED</span>
              <span className={`text-[10px] sm:text-xs font-bold tracking-wider px-2.5 sm:px-3 py-1 rounded-full ${tool.badgeColor} shadow-lg`}>
                {tool.badge}
              </span>
              {tool.socialProof && (
                <span className="text-[10px] sm:text-xs font-bold tracking-wider px-2.5 sm:px-3 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white ring-1 ring-white/40">
                  🔥 {socialProofLive}
                </span>
              )}
            </div>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center ${tool.iconBg} backdrop-blur-sm ring-2 ring-white/40 group-hover:scale-110 transition-transform duration-300 shrink-0`}>
                <Icon size={24} strokeWidth={1.8} className="sm:hidden text-white drop-shadow" />
                <Icon size={32} strokeWidth={1.8} className="hidden sm:block text-white drop-shadow" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif-display text-2xl sm:text-4xl font-medium sm:font-light leading-tight tracking-tight drop-shadow-sm">
                  {tool.title}
                </h3>
                <p className="text-sm sm:text-base font-light mt-2 leading-snug text-white/95 line-clamp-2 sm:line-clamp-none">
                  {tool.pitch}
                </p>
                {tool.price && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-white/70 line-through text-sm sm:text-base">{tool.anchorPrice}</span>
                    <span className="text-white font-serif-display text-2xl sm:text-3xl">{tool.price}</span>
                    <span className="text-[10px] sm:text-xs uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/25 backdrop-blur-sm">One-time</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-auto pt-4 sm:pt-5 flex items-center gap-1.5 text-xs sm:text-sm font-bold tracking-wider opacity-95 group-hover:opacity-100">
              <span className="uppercase">{cta}</span>
              <ArrowRight size={14} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${tool.iconBg} backdrop-blur-sm ring-1 ring-white/30 group-hover:scale-110 transition-transform duration-300`}>
                <Icon size={18} strokeWidth={1.8} className="sm:hidden text-white drop-shadow" />
                <Icon size={24} strokeWidth={1.8} className="hidden sm:block text-white drop-shadow" />
              </div>
              <div className="flex flex-col items-end gap-1">
                {isPaid ? (
                  <span className={`text-[9px] sm:text-[10px] font-bold tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full ${tool.badgeColor} shadow whitespace-nowrap flex items-center gap-1`}>
                    {tool.anchorPrice && (
                      <span className="line-through opacity-60 font-normal">{tool.anchorPrice}</span>
                    )}
                    <span>{tool.price}</span>
                  </span>
                ) : (
                  <span className={`text-[9px] sm:text-[10px] font-bold tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full ${tool.badgeColor} shadow whitespace-nowrap`}>
                    {tool.badge}
                  </span>
                )}
              </div>
            </div>

            <h3 className="font-serif-display text-sm sm:text-2xl font-medium sm:font-light mt-2 sm:mt-4 leading-tight tracking-tight drop-shadow-sm line-clamp-2">
              {tool.title}
            </h3>
            <p className="text-[11px] sm:text-sm font-light mt-1 sm:mt-2 leading-snug text-white/90 line-clamp-2 sm:line-clamp-3">
              {tool.pitch}
            </p>

            {tool.socialProof && (
              <div className="mt-2 text-[9px] sm:text-[10px] font-semibold tracking-wide text-white/85 flex items-center gap-1">
                <span>🔥</span>
                <span className="truncate">{socialProofLive}</span>
              </div>
            )}

            <div className="mt-auto pt-2 sm:pt-3 flex items-center gap-1 text-[10px] sm:text-xs font-bold tracking-wider opacity-95 group-hover:opacity-100">
              <span className="uppercase truncate">{cta}</span>
              <ArrowRight size={12} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform shrink-0" />
            </div>
          </>
        )}
      </div>
    </Link>
  );
}

function CategoryBar({ activeCategory, setActiveCategory, counts }) {
  return (
    <div className="flex flex-wrap gap-1.5 sm:gap-2" data-testid="category-bar">
      <button
        type="button"
        onClick={() => setActiveCategory("all")}
        data-testid="cat-pill-all"
        className={`px-2.5 sm:px-4 py-1 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold tracking-wider transition-all border ${
          activeCategory === "all"
            ? "bg-[#3F2D1B] text-amber-50 border-[#3F2D1B] shadow-md"
            : "bg-white text-[#3F2D1B] border-amber-300 hover:border-[#3F2D1B]"
        }`}
      >
        ALL · {Object.values(counts).reduce((a, b) => a + b, 0)}
      </button>
      {CATEGORIES.map((c) => {
        const Icon = c.icon;
        const active = activeCategory === c.key;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => setActiveCategory(c.key)}
            data-testid={`cat-pill-${c.key}`}
            className={`px-2.5 sm:px-3.5 py-1 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold tracking-wider transition-all border flex items-center gap-1 sm:gap-1.5 ${
              active
                ? `text-white border-transparent shadow-md bg-gradient-to-r ${c.color}`
                : "bg-white text-[#3F2D1B] border-amber-300 hover:border-[#3F2D1B]"
            }`}
          >
            {/* Icon shows on desktop only — saves ~24px per pill on mobile so 7 pills fit in 2 rows */}
            <Icon size={13} strokeWidth={2} className="hidden sm:block" />
            {/* Short label on mobile, full label on desktop */}
            <span className="sm:hidden">{c.shortLabel.toUpperCase()} · {counts[c.key] || 0}</span>
            <span className="hidden sm:inline">{c.label.toUpperCase()} · {counts[c.key] || 0}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function AllToolsPage() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const counts = useMemo(() => {
    const m = {};
    TOOLS.forEach((t) => { m[t.category] = (m[t.category] || 0) + 1; });
    return m;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TOOLS.filter((t) => {
      if (activeCategory !== "all" && t.category !== activeCategory) return false;
      if (q && !(`${t.title} ${t.pitch}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [query, activeCategory]);

  const sectioned = useMemo(() => {
    // Separate "pinned" tools (e.g. 20-Year Forecast) so they always appear
    // at the very top of the page in a "Featured" pseudo-section, ahead of
    // every natural category. They're then excluded from their normal
    // category section so the same tile doesn't appear twice.
    const pinned = filtered.filter((t) => t.pinned);
    const rest   = filtered.filter((t) => !t.pinned);

    if (activeCategory !== "all" || query) {
      // When the user has filtered/searched, show pinned hits + the rest
      // flat (no per-category sub-sections).
      return [
        ...(pinned.length ? [{ category: { key: "featured", label: "Featured", tag: "Start here", color: "from-amber-400 to-orange-500", icon: Stars }, tools: pinned }] : []),
        { category: null, tools: rest },
      ];
    }

    return [
      ...(pinned.length ? [{ category: { key: "featured", label: "Featured", tag: "Start here", color: "from-amber-400 to-orange-500", icon: Stars }, tools: pinned }] : []),
      ...CATEGORIES.map((c) => ({
        category: c,
        tools: rest.filter((t) => t.category === c.key),
      })).filter((s) => s.tools.length > 0),
    ];
  }, [filtered, activeCategory, query]);

  return (
    <PageShell>
      <SEO
        title="Every Vedic Astrology Tool — Kundali, Dasha, Navamsa, Celebrity Charts & More"
        description="Every classical Vedic astrology tool in one hub: Free Kundali, Kundali Matching, Navamsa, Dasha Calculator, Kalachakra Dasha, Bhrigu Bindu, Manglik/Kalsarp Dosha, Varshaphal, Past-life, Numerology, Ishta Devata, Muhurat, Wealth Hora, KP Astrology, Astro Visualise, Celebrity Chart Database and Celebrity Lookalike. Swiss-Ephemeris accurate."
        path="/tools"
        keywords={[
          "vedic astrology tools", "free astrology calculator", "kundali tools",
          "online astrology calculator", "navamsa calculator", "dasha calculator",
          "kalachakra dasha", "bhrigu bindu", "manglik dosha calculator",
          "kalsarp dosha", "sade sati calculator", "varshaphal calculator",
          "muhurat finder", "ishta devata", "numerology calculator",
          "past life astrology", "raja yogas", "ashtakavarga calculator",
          "celebrity birth charts", "celebrity lookalike", "kp astrology",
          "wealth hora", "astro visualise", "d2 chart",
        ]}
      />
      <style>{`
        @keyframes tool-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer-band {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>

      {/* ─────────── HERO ───────────
          Mobile: minimalist — single H2 title + search + category pills only,
          so the seeker lands directly on tool tiles. Desktop keeps the
          richer flagship hero (eyebrow + huge serif title + shimmer halo).
      */}
      <section
        className="relative overflow-hidden"
        data-testid="all-tools-hero"
      >
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-br from-[#FFF1D6] via-[#FFE5C2] to-[#FAD0A3]"
        />
        <div
          className="absolute inset-0 -z-10 opacity-[0.18] mix-blend-multiply"
          style={{ backgroundImage: "radial-gradient(#A0522D 0.7px, transparent 0.7px)", backgroundSize: "14px 14px" }}
        />
        {/* shimmer halo — desktop only (sm: shows it back) */}
        <div
          className="hidden sm:block absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-30"
          style={{
            background: "conic-gradient(from 0deg, #f59e0b, #ec4899, #8b5cf6, #06b6d4, #f59e0b)",
            animation: "shimmer-band 16s linear infinite",
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 sm:pt-16 pb-4 sm:pb-10">
          {/* Desktop-rich eyebrow + serif headline. Hidden on mobile so the
              page jumps straight to title + search + tiles. */}
          <div className="hidden sm:block">
            <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2 mb-4">
              <Music2 size={14} strokeWidth={2} /> The full AstroKuber toolkit
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-[#3F2D1B] max-w-4xl">
              Every reading. <span className="text-gold italic">One vivid map.</span>
            </h1>
            <p className="text-[#3F2D1B] text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
              <span className="font-bold">{TOOLS.length} hand-built calculators</span> — from your first kundali to <span className="font-bold text-gold italic">flagship Kalachakra Dasha</span>. <span className="text-[#3F2D1B]/75 font-light">Free where it should be, paywalled where the depth justifies it, color-coded by life-domain so you always know where to start.</span>
            </p>
          </div>

          {/* Mobile-only compact title row */}
          <div className="sm:hidden">
            <h1 className="font-serif-display text-2xl font-medium leading-tight text-[#3F2D1B]" data-testid="all-tools-mobile-title">
              All Innovative Tools
            </h1>
            <p className="text-[11px] text-[#7C2D12] mt-1 font-semibold tracking-wider uppercase">
              {TOOLS.length} calculators &middot; tap any tile to begin
            </p>
          </div>

          {/* search */}
          <div className="mt-3 sm:mt-8 max-w-2xl">
            <div className="relative">
              <Search size={16} strokeWidth={2} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C2D12]/60" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools — try 'marriage', 'wealth', 'lottery', 'dasha'…"
                data-testid="all-tools-search"
                className="w-full pl-11 pr-4 py-2.5 sm:py-3.5 rounded-full bg-white/90 backdrop-blur border-2 border-amber-300 text-sm placeholder:text-[#7C2D12]/40 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B] focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="mt-3 sm:mt-5">
            <CategoryBar
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              counts={counts}
            />
          </div>
        </div>
      </section>

      {/* ─────────── TOOL GRID ─────────── */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 py-5 sm:py-14 space-y-8 sm:space-y-14 pb-28 sm:pb-14">
        {sectioned.length === 0 && (
          <div className="text-center py-20" data-testid="empty-state">
            <Lock size={32} strokeWidth={1.5} className="mx-auto text-[#7C2D12]/40" />
            <p className="font-serif-display text-2xl mt-3 font-light text-[#3F2D1B]">No tools match this filter.</p>
            <p className="text-sm text-[#3F2D1B]/60 mt-1">Try widening your search or pick a different category.</p>
          </div>
        )}

        {sectioned.map(({ category, tools }, sectionIdx) => (
          <section key={category?.key || "results"} data-testid={category ? `cat-section-${category.key}` : "search-results"}>
            {category && (
              <header className="mb-3 sm:mb-6 flex items-baseline justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center shadow flex-shrink-0`}>
                    <category.icon size={14} strokeWidth={1.8} className="sm:hidden text-white" />
                    <category.icon size={18} strokeWidth={1.8} className="hidden sm:block text-white" />
                  </div>
                  <div>
                    <div className="label-eyebrow text-[#7C2D12] hidden sm:block">{category.tag}</div>
                    <h2 className="font-serif-display text-lg sm:text-3xl font-medium sm:font-light text-[#3F2D1B] leading-tight">
                      {category.label}
                    </h2>
                  </div>
                </div>
                <span className="text-[10px] sm:text-xs text-[#3F2D1B]/60 font-medium">
                  {tools.length} {tools.length === 1 ? "tool" : "tools"}
                </span>
              </header>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
              {tools.map((tool, i) => (
                <ToolCard key={tool.slug} tool={tool} index={sectionIdx * 8 + i} />
              ))}
            </div>
          </section>
        ))}

        {/* ─────── FOOTER CTA STRIP ─────── */}
        <section
          className="relative rounded-3xl overflow-hidden p-8 sm:p-12 text-center"
          data-testid="talk-to-astro-cta"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#3F2D1B] via-[#5C3A1B] to-[#8B4513]" />
          <div
            className="absolute inset-0 opacity-25 mix-blend-overlay"
            style={{ backgroundImage: "radial-gradient(rgba(255,200,120,0.8) 0.6px, transparent 0.6px)", backgroundSize: "10px 10px" }}
          />
          <div className="relative">
            <ScrollText size={24} strokeWidth={1.5} className="mx-auto text-amber-200" />
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light text-amber-50 mt-3 leading-tight">
              Want a human to walk you through?
            </h2>
            <p className="text-amber-100/80 text-sm sm:text-base mt-3 max-w-2xl mx-auto font-light leading-relaxed">
              Every tool above has a live astrologer who specialises in it. Get a 1:1 reading from a Jaimini, Vedic or Numerology specialist — based on the exact tools that matter to you.
            </p>
            <Link
              to="/astrologers"
              data-testid="cta-browse-astrologers"
              className="inline-flex items-center gap-2 mt-7 px-7 py-3 rounded-full bg-gradient-to-r from-amber-300 to-yellow-400 text-amber-950 font-bold text-sm tracking-wider hover:shadow-lg hover:scale-105 transition-all"
            >
              <Briefcase size={16} strokeWidth={2} />
              BROWSE ASTROLOGERS
              <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </section>
      </main>
    </PageShell>
  );
}
